import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import Button, { ButtonSizes, ButtonTypes } from '@Components/Buttons/Button';
import FormField from '@Components/forms/FormField';
import TextInput, { TextInputProps } from '@Components/forms/inputs/TextInput';
import HorizontalRule from '@Components/HorizontalRule';
import InfoMessage, { MessageTypes } from '@Components/InfoMessage';
import SocialLogins from '@Components/SocialLogins';
import { Messages } from '@Config/messages';
import { useTranslations } from '@Hooks/useTranslations';
import { LoginActionData, LoginFormData } from '@Store/auth/auth.types';
import { validate, validateEmail, validateRequired } from '@Utils/form';
import { LocationReferer } from '@Utils/types';

enum LoginFormFields {
  email = 'email',
  password = 'password',
}

interface LoginFormProps extends LocationReferer {
  onSubmit: (data: LoginActionData) => void;
  initialValues: LoginFormData;
}

const renderForm = ({
  handleSubmit,
  submitting,
  submitError,
  referer,
}: FormRenderProps<LoginFormData> & LocationReferer): ReactNode => {
  const t = useTranslations();

  return (
    <form onSubmit={handleSubmit}>
      <InfoMessage type={MessageTypes.error} message={submitError} />
      <FormField<TextInputProps<string>>
        name={LoginFormFields.email}
        type="email"
        component={TextInput}
        placeholder={Messages.labelEmail}
        validate={validate(validateEmail(t), validateRequired(t))}
      />
      <FormField<TextInputProps<string>>
        name={LoginFormFields.password}
        component={TextInput}
        type="password"
        placeholder={Messages.labelPassword}
        validate={validateRequired(t)}
      />
      <Button
        loading={submitting}
        label={t(Messages.login)}
        disabled={submitting}
        size={ButtonSizes.fill}
        type={ButtonTypes.submit}
      />
      <HorizontalRule msg={t(Messages.labelOr)} marginTop />
      <SocialLogins addNaturalCompany referer={referer} />
    </form>
  );
};

const LoginForm: React.FC<LoginFormProps> = ({ initialValues, onSubmit, referer }) => (
  <Form<LoginFormData>
    onSubmit={data => onSubmit({ ...data, referer })}
    initialValues={initialValues}
    render={props => renderForm({ ...props, referer })}
  />
);

export default LoginForm;
